"use client";

import { useEffect } from "react";
import useSWR from "swr";
import { fillinMissingDateInCalendar, gettingPlaceHolderCalendar } from "commonModule/services/calendar";
import useCalendarCoverage from "hooks/swr/useCalendarCoverage";
import { apiClientCommon } from "services/apiClientCommon";
import { selectUser } from "stores/auth/authSlice";
import { markBookingStore } from "stores/booking/bookingSlice";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { canUseDOM } from "utils/dom";
const calendarDataFetcher = async ({
  propertyId,
  showSameDay
}: {
  propertyId: string;
  url: string;
  showSameDay?: boolean;
}) => {
  return (await apiClientCommon.getPropertyCalendar({
    params: {
      propertyId,
      ...(showSameDay ? {
        showSameDay: true
      } : {})
    }
  })).data?.data;
};
const GetCalendarDataLogic = ({
  property_id
}: {
  property_id: string;
}) => {
  const dispatch = useAppDispatch();
  const {
    data
  } = useCalendarCoverage();
  const coverage = data?.coverage ?? 180;
  const precisionPricingAllowedDays = data?.precision_pricing_beyond_days ?? 29;
  const maxCancellationFee = data?.max_cancellation_fee ?? 0;

  // Set precision pricing allowed days in the store
  useEffect(() => {
    dispatch(markBookingStore({
      precisionPricingAllowedDays: precisionPricingAllowedDays,
      maxCancellationFee: maxCancellationFee
    }));
  }, [dispatch, precisionPricingAllowedDays, maxCancellationFee]);
  const user = useAppSelector(selectUser);
  const isAdmin = user?.isAdmin;
  const isSameDayCheckin = (() => {
    if (!canUseDOM()) return false;
    if (isAdmin) return true;
    const params = new URLSearchParams(window.location.search);
    const checkinDate = params.get("check_in");
    const allowSameDay = params.get("allowSameDay") === "true";

    // ✅ If allowSameDay param is set, always show today
    if (allowSameDay) return true;
    if (!checkinDate) return false;
    const today = new Date();
    const selected = new Date(checkinDate);

    // ✅ Compare local date parts, not timestamps
    return selected.getFullYear() === today.getFullYear() && selected.getMonth() === today.getMonth() && selected.getDate() === today.getDate();
  })();
  const {
    data: calendarData
  } = useSWR(property_id ? {
    url: "/api/toGetCalendarData",
    propertyId: property_id,
    showSameDay: isSameDayCheckin // ✅ added
  } : null, calendarDataFetcher, {
    keepPreviousData: true,
    dedupingInterval: 10000,
    // if within 10 sec another request comes then ignore it
    revalidateOnFocus: false,
    errorRetryCount: 3
  });
  useEffect(() => {
    if (calendarData?.calendarRate) {
      dispatch(markBookingStore({
        calendarRate: calendarData?.calendarRate
      }));
    }
  }, [calendarData?.calendarRate, dispatch]);
  useEffect(() => {
    if (canUseDOM()) {
      const search = window.location.search;
      const params = new URLSearchParams(search);
      const dataToken = (params.get("dataToken") as string);
      if ((calendarData?.calendar && calendarData?.calendar?.length == 0 || !calendarData?.active || calendarData?.isHide) && !(dataToken === "E76xOs7B2s0")) {
        dispatch(markBookingStore({
          calendarRateList: gettingPlaceHolderCalendar(coverage || 120),
          maximizeProfit: calendarData?.maxProfit
        }));
      } else if (calendarData?.calendar) {
        dispatch(markBookingStore({
          calendarRateList: fillinMissingDateInCalendar(calendarData?.calendar, coverage || 120) || [],
          maximizeProfit: calendarData?.maxProfit
        }));
      }
    }
  }, [calendarData?.active, calendarData?.calendar, calendarData?.calendar?.length, calendarData?.isHide, calendarData?.maxProfit, coverage, dispatch]);
  useEffect(() => {
    dispatch(markBookingStore({
      calendarData: {
        averagePrice: calendarData?.averagePrice || 100,
        checkArrivalDeparture: calendarData?.checkArrivalDeparture || false
      }
    }));
  }, [calendarData?.averagePrice, calendarData?.checkArrivalDeparture, dispatch]);
  useEffect(() => {
    dispatch(markBookingStore({
      isTravelProtected: false
    }));
  }, [dispatch]);
  return null;
};
export default GetCalendarDataLogic;